import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import { postChangeRole } from "@/services/Settings";
import { CurrentCompany, MembershipType, UserRole, UserRoleType } from "@/types";
import UserMembershipTypeEnum from "@/constants/enums/UserMembershipTypeEnum";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";

interface RoleSelectorProps {
  userId: number;
  currentRole: string;
  currentCompany: CurrentCompany | undefined;
  disabled?: boolean;
  onClose: () => void;
}

const RoleSelector: React.FC<RoleSelectorProps> = ({
  userId,
  currentRole,
  currentCompany,
  disabled = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /*NOTE TO SELF => fix infinite loop on thomas.s and test.test, also rerendering blinking of screen */
  const roleOptions = useMemo(() => {
    const roleData = Object.values(UserMembershipTypeEnum).map((role) => ({
      value: role,
      label: role,
    }));

    return transformDataToSelectOption(roleData, "value", "label", t, "enumerables:roleType");
  }, [t]);

  const { mutateAsync: updateUserRole, isPending } = useMutation({
    mutationFn: async (roleType: string) => {
      const role: UserRole = {
        companyId: currentCompany?.companyId as number,
        roleType: roleType as UserRoleType,
        companyName: currentCompany?.companyName as string,
        companyMemberShip: currentCompany?.companyMembershipType as MembershipType,
      };
      await postChangeRole(userId, role);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyTeam", currentCompany?.companyId],
      });
      onClose();
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:settings.userRoleUpdated"))
      );
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:settings.userRoleUpdateError"))
      );
    },
  });

  const handleRoleChange = useCallback(
    async (newRole: string | null) => {
      if (!newRole || newRole === currentRole) {
        return;
      }
      await updateUserRole(newRole);
    },
    [currentRole, updateUserRole]
  );

  return (
    <Select
      id={`role-selector-${userId}`}
      data={roleOptions}
      value={currentRole}
      onChange={handleRoleChange}
      loading={isPending}
      variant="filled"
      searchable={false}
      disabled={disabled}
    />
  );
};

export default RoleSelector;
